import Swal from "sweetalert2";
import { toast } from "react-toastify";

import { getCookie } from "../store/localStorage";
import { replaceHierarchyTermsWithAlias } from "./commonUtils";

// Commented to not replace words based on hierarchy
// const SWAL_TEXT_FIELDS = [
//     "title",
//     "text",
//     "html",
//     "footer",
//     "confirmButtonText",
//     "cancelButtonText",
//     "denyButtonText",
// ];

// const TOAST_METHODS = ["success", "error", "info", "warn", "warning", "dark", "loading"];
// const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT"]);
// const LABEL_TAGS = new Set(["LABEL", "BUTTON", "LEGEND", "TH", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "SPAN", "P", "SMALL"]);
// const LABEL_ROLES = new Set(["tab", "button", "heading", "alert", "li"]);
// const VALIDATION_MESSAGE_REGEX = /required|validation|invalid|must\s+be|cannot\s+be\s+empty|is\s+required|please\s+(enter|select|choose|provide)/i;
// const PLACEHOLDER_TEXT_REGEX = /^(select|choose|enter)\b/i;
// const VALUE_REGION_SELECTOR = "tbody, td, [role='cell'], [role='gridcell'], [role='row'], option, [role='option'], [role='listbox'], [data-no-alias='true']";
// const ATTRIBUTE_SELECTOR = "[title], [aria-label], [placeholder], [single-select__placeholder]";

const SWAL_TEXT_FIELDS = [];

const TOAST_METHODS = [];
const SKIP_TAGS = new Set([]);
const LABEL_TAGS = new Set([]);
const LABEL_ROLES = new Set([]);
const VALIDATION_MESSAGE_REGEX = /required|validation|invalid|must\s+be|cannot\s+be\s+empty|is\s+required|please\s+(enter|select|choose|provide)/i;
const PLACEHOLDER_TEXT_REGEX = /^(select|choose|enter)\b/i;
const VALUE_REGION_SELECTOR = "";
const ATTRIBUTE_SELECTOR = "";

const aliasText = (value, userData) => {
    if (typeof value !== "string") {
        return value;
    }

    return replaceHierarchyTermsWithAlias(value, userData);
};

const aliasSwalOptions = (options, userData) => {
    if (!options || typeof options !== "object") {
        return options;
    }

    const updatedOptions = { ...options };

    SWAL_TEXT_FIELDS.forEach((field) => {
        if (typeof updatedOptions[field] === "string") {
            updatedOptions[field] = aliasText(updatedOptions[field], userData);
        }
    });

    return updatedOptions;
};

const patchSweetAlert = () => {
    if (Swal.__hierarchyAliasPatched) {
        return;
    }

    const originalFire = Swal.fire.bind(Swal);

    Swal.fire = (...args) => {
        const userData = getCookie("userData");

        if (args.length === 1 && args[0] && typeof args[0] === "object") {
            return originalFire(aliasSwalOptions(args[0], userData));
        }

        const aliasedArgs = args.map((arg) => aliasText(arg, userData));
        return originalFire(...aliasedArgs);
    };

    Swal.__hierarchyAliasPatched = true;
};

const patchToastMethods = () => {
    if (toast.__hierarchyAliasPatched) {
        return;
    }

    TOAST_METHODS.forEach((method) => {
        if (typeof toast[method] !== "function") {
            return;
        }

        const originalMethod = toast[method].bind(toast);

        toast[method] = (message, ...rest) => {
            const userData = getCookie("userData");
            return originalMethod(aliasText(message, userData), ...rest);
        };
    });

    if (typeof toast.promise === "function") {
        const originalPromise = toast.promise.bind(toast);

        toast.promise = (promise, messages, options) => {
            const userData = getCookie("userData");

            if (!messages || typeof messages !== "object") {
                return originalPromise(promise, messages, options);
            }

            const updatedMessages = { ...messages };

            ["pending", "success", "error"].forEach((key) => {
                const message = updatedMessages[key];

                if (typeof message === "string") {
                    updatedMessages[key] = aliasText(message, userData);
                    return;
                }

                if (message && typeof message === "object" && typeof message.render === "string") {
                    updatedMessages[key] = {
                        ...message,
                        render: aliasText(message.render, userData),
                    };
                }
            });

            return originalPromise(promise, updatedMessages, options);
        };
    }

    toast.__hierarchyAliasPatched = true;
};

const isInValueRegion = (element) => {
    if (!element) {
        return false;
    }

    return !!element.closest(VALUE_REGION_SELECTOR);
};

const isSkippableElement = (element) => {
    if (!element || !element.tagName) {
        return true;
    }

    return SKIP_TAGS.has(element.tagName.toUpperCase());
};

const shouldAliasTextNode = (textNode) => {
    const parent = textNode?.parentElement;
    const text = textNode?.textContent;

    if (!parent || typeof text !== "string" || !text.trim()) {
        return false;
    }

    if (isSkippableElement(parent) || isInValueRegion(parent)) {
        return false;
    }

    const tagName = parent.tagName.toUpperCase();
    if (LABEL_TAGS.has(tagName)) {
        return true;
    }

    const role = String(parent.getAttribute("role") || "").toLowerCase();
    if (LABEL_ROLES.has(role)) {
        return true;
    }

    if (parent.getAttribute("data-bs-toggle") === "tab") {
        return true;
    }

    // Generic support for dropdown placeholder text rendered as plain div/span text.
    if (PLACEHOLDER_TEXT_REGEX.test(text)) {
        return true;
    }

    return VALIDATION_MESSAGE_REGEX.test(text);
};

const shouldAliasAttribute = (element, attr) => {
    if (!element || isSkippableElement(element)) {
        return false;
    }

    if (element.closest("[data-no-alias='true']")) {
        return false;
    }

    // Placeholder should always be aliased as requested.
    if (attr === "placeholder") {
        return true;
    }

    if (isInValueRegion(element)) {
        return false;
    }

    const tagName = element.tagName.toUpperCase();
    if (LABEL_TAGS.has(tagName)) {
        return true;
    }

    const role = String(element.getAttribute("role") || "").toLowerCase();
    if (LABEL_ROLES.has(role)) {
        return true;
    }

    return false;
};

const applyComponentAliasesToDom = (root = document) => {
    if (!root) {
        return;
    }

    const scope = root.nodeType === Node.DOCUMENT_NODE ? root.body : root;
    if (!scope || typeof scope.querySelectorAll !== "function") {
        return;
    }

    const userData = getCookie("userData");
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
        const textNode = walker.currentNode;
        if (!shouldAliasTextNode(textNode)) {
            continue;
        }

        const original = textNode.textContent;
        const replaced = aliasText(original, userData);
        if (replaced !== original) {
            textNode.textContent = replaced;
        }
    }

    scope.querySelectorAll(ATTRIBUTE_SELECTOR).forEach((element) => {
        ["title", "aria-label", "placeholder"].forEach((attr) => {
            if (!shouldAliasAttribute(element, attr)) {
                return;
            }

            const value = element.getAttribute(attr);
            if (typeof value !== "string" || !value.trim()) {
                return;
            }

            const replaced = aliasText(value, userData);
            if (replaced !== value) {
                element.setAttribute(attr, replaced);
            }
        });
    });
};

const patchComponentLabels = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (document.__hierarchyComponentAliasPatched) {
        applyComponentAliasesToDom(document);
        return;
    }

    const scheduleComponentAliasPass = (() => {
        let scheduled = false;

        return () => {
            if (scheduled) {
                return;
            }

            scheduled = true;
            window.requestAnimationFrame(() => {
                scheduled = false;
                applyComponentAliasesToDom(document);
            });
        };
    })();

    applyComponentAliasesToDom(document);

    const observer = new MutationObserver(() => {
        scheduleComponentAliasPass();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
    });

    document.__hierarchyComponentAliasObserver = observer;
    document.__hierarchyComponentAliasPatched = true;
};

export const applyHierarchyAliasGlobalPatches = () => {
    patchSweetAlert();
    patchToastMethods();
    patchComponentLabels();
};
